import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createApiClient } from "../api/client";
import { Family } from "../types";
import Modal from "../components/Modal";
import StartPlanFlow from "../components/StartPlanFlow";
import FamilyManagePanel from "../components/FamilyManagePanel";
import Button from "../components/ui/Button";
import Reveal from "../components/motion/Reveal";

const textableCount = (f: Family) => f.members.filter((m) => m.mobileNumber).length;

const INPUT =
  "rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2.5 text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

const DashboardPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newFamilyName, setNewFamilyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");

  const [manageId, setManageId] = useState<string | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [startFamilyId, setStartFamilyId] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).get<Family[]>("/api/families");
      setFamilies(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Couldn't load your families. Is the API running?");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  const createFamily = async () => {
    if (!newFamilyName.trim()) return;
    setCreating(true);
    try {
      const token = await getAccessTokenSilently();
      await createApiClient(token).post<Family>("/api/families", { name: newFamilyName.trim() });
      setNewFamilyName("");
      await load();
    } finally {
      setCreating(false);
    }
  };

  const filtered = useMemo(
    () => families.filter((f) => f.name.toLowerCase().includes(query.trim().toLowerCase())),
    [families, query]
  );
  const manageFamily = families.find((f) => f.id === manageId) ?? null;

  const openStart = (familyId?: string) => {
    setStartFamilyId(familyId);
    setManageId(null);
    setStartOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-ink">Your families</h1>
          <p className="mt-1 text-ink/60">Pick a group to start tonight's plan — or manage who's in each.</p>
        </div>
        {families.length > 0 && (
          <Button className="shrink-0" onClick={() => openStart()}>
            Start a plan
          </Button>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
      )}

      {/* Create a family */}
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <input
          value={newFamilyName}
          onChange={(e) => setNewFamilyName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createFamily()}
          placeholder="New family name (e.g. The Hoinowskis)"
          className={`${INPUT} flex-1`}
        />
        <Button variant="secondary" onClick={createFamily} disabled={creating || !newFamilyName.trim()}>
          Add family
        </Button>
      </div>

      {families.length > 6 && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search families…"
          className={`${INPUT} mt-3 w-full text-sm`}
        />
      )}

      {families.length === 0 ? (
        <p className="mt-12 text-center text-ink/50">No families yet — add one above to get started.</p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f, i) => (
            <Reveal key={f.id} index={i}>
              <div className="flex h-full flex-col rounded-3xl border border-primary-100 bg-white/70 p-5 shadow-warm backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1">
                <button onClick={() => setManageId(f.id)} className="flex-1 text-left">
                  <span className="flex items-center gap-2">
                    <span className="font-display text-lg font-semibold text-ink">{f.name}</span>
                    {!f.isOwner && (
                      <span className="rounded-full bg-cream-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink/55">
                        Member
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs text-ink/55">
                    {f.members.length} {f.members.length === 1 ? "person" : "people"} · {textableCount(f)} textable
                  </span>
                </button>
                <div className="mt-4 space-y-2">
                  <Button fullWidth onClick={() => openStart(f.id)}>
                    Start a plan
                  </Button>
                  <div className="flex gap-2">
                    <Link
                      to={`/families/${f.id}/plans`}
                      className="flex-1 rounded-full bg-cream-200 px-4 py-2 text-center text-sm font-semibold text-ink ring-1 ring-primary-200/70 hover:bg-cream"
                    >
                      Plans
                    </Link>
                    <button
                      onClick={() => setManageId(f.id)}
                      className="flex-1 rounded-full bg-cream-200 px-4 py-2 text-sm font-semibold text-ink ring-1 ring-primary-200/70 hover:bg-cream"
                    >
                      Manage
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-full text-sm text-ink/40">No families match "{query}".</p>
          )}
        </div>
      )}

      {/* Manage one family */}
      <Modal open={!!manageFamily} onClose={() => setManageId(null)} title={manageFamily?.name ?? ""}>
        {manageFamily && (
          <FamilyManagePanel
            family={manageFamily}
            onChanged={load}
            onStart={() => openStart(manageFamily.id)}
          />
        )}
      </Modal>

      {/* Start a plan */}
      <StartPlanFlow
        open={startOpen}
        onClose={() => {
          setStartOpen(false);
          load();
        }}
        initialFamilyId={startFamilyId}
      />
    </div>
  );
};

export default DashboardPage;
